import { t } from "@lingui/macro";
import * as Sentry from "@sentry/react";
import { WinampLogoFilled } from "@winamp/icons/WinampLogoFilled.js";
import Cookies from "js-cookie";
import { type ReactNode, Suspense, useEffect, useState } from "react";
import type { ClientLoaderFunctionArgs, LinksFunction } from "react-router";
import {
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  isRouteErrorResponse,
  redirect,
  useRouteError,
} from "react-router";
import appStylesheetUrl from "~/app.css?url";
import { AppErrorBoundary } from "./components/AppErrorBoundary";
import { TrackingScripts } from "./components/TrackingScripts";
import { useClientId } from "./hooks/useClientId";
import { useLocale } from "./modules/lingui/lingui";
import { isMobile } from "./modules/support/clientId";
import { getGlobals } from "./utils/get-globals";
import { getSubmissionResult } from "./utils/get-submission-result";

export const links: LinksFunction = () => {
  return [{ rel: "stylesheet", href: appStylesheetUrl }];
};

export function clientLoader({ request }: ClientLoaderFunctionArgs) {
  const url = new URL(request.url);

  if (url.pathname === "/") {
    return redirect("/login");
  }

  const globals = getGlobals();

  const cookieTheme = Cookies.get("theme") ?? "";

  let theme = ["light", "dark"].includes(cookieTheme)
    ? cookieTheme
    : isMobile(globals?.clientId ?? "")
      ? "dark"
      : undefined;

  if (!theme) {
    theme = window.matchMedia("(prefers-color-scheme: dark)").matches
      ? "dark"
      : "light";
  }

  if (theme === "dark") {
    document.documentElement.classList.add("dark");
  }

  const utmSource = url.searchParams.get("utm_source");
  if (utmSource) {
    Cookies.set("utm_source", utmSource);
  }

  return { lastResult: getSubmissionResult(), globals, theme };
}

export function Layout({ children }: { children: ReactNode }) {
  const locale = useLocale();

  return (
    <html
      lang={locale ?? "en"}
      translate="no"
      className="bg-surface-canvas overflow-x-hidden"
    >
      <head>
        <meta charSet="utf-8" />
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1, maximum-scale=1, user-scalable=0"
        />
        <TrackingScripts />
        <Meta />
        <Links />
      </head>
      <body>
        <noscript>
          <iframe
            title="Google Tag Manager"
            src={`https://www.googletagmanager.com/ns.html?id=${import.meta.env.VITE_GTM_ID}`}
            height="0"
            width="0"
            className="hidden invisible"
          />
        </noscript>
        <div className="overflow-hidden">
          {children}
          <ScrollRestoration />
          <Scripts />
        </div>
      </body>
    </html>
  );
}

export default function App() {
  const { pages } = useClientId();
  const [isLoading, setIsLoading] = useState(Cookies.get("loaded") !== "1");

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
      if (Cookies.get("loaded") !== "1") {
        Cookies.set("loaded", "1");
      }
    }, 350);

    return () => clearTimeout(timer);
  }, []);

  return (
    <Suspense fallback={isLoading ? <Fallback /> : null}>
      {isLoading ? (
        <Fallback />
      ) : (
        <pages.Root>
          <Outlet />
        </pages.Root>
      )}
    </Suspense>
  );
}

function Fallback() {
  return (
    <div className="z-20 bg-surface-canvas absolute inset-0 flex items-center justify-center text-brand-700">
      <WinampLogoFilled className="size-[174px] animate-pulse" />
    </div>
  );
}

export function HydrateFallback() {
  return (
    <div className="text-primary font-sans antialiased w-screen h-screen" />
  );
}

export function ErrorBoundary() {
  const error = useRouteError();
  Sentry.captureException(error);

  if (isRouteErrorResponse(error)) {
    return (
      <Layout>
        <AppErrorBoundary title={`${error.status} ${error.statusText}`} />
      </Layout>
    );
  }

  if (error instanceof Error) {
    return (
      <Layout>
        <AppErrorBoundary title={t`Unexpected Error`} message={error.message}>
          {import.meta.env.DEV && (
            <p className="mt-4 break-words text-xs">{error.stack}</p>
          )}
        </AppErrorBoundary>
      </Layout>
    );
  }

  return (
    <Layout>
      <AppErrorBoundary title={t`Unexpected Error`} />
    </Layout>
  );
}
